// --------------------------------------------------------------
// Created On: 2025-05-16
// Author: Zachary Thomas
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect } from "react";
import { API, GRAPH_HEIGHT_FOR_CARD } from "../../../../constants/miscellaneous";
import { useParams } from "react-router-dom";
import Spinner from "../../../../components/Spinner/Spinner";
import Error from "../../../../components/Error/Error";
import Warning from "../../../../components/Warning/Warning";
import LineChart from "../../../../components/LineChart/LineChart";
import AccumulationChart from "../../../../components/AccumulationChart/AccumulationChart";
import isoAddDays from "../../../../utilities/time/isoAddDays";
import isoToDate from "../../../../utilities/time/isoToDate";
import isoToLocalDate from "../../../../utilities/time/isoToLocalDate";
import nowToLocalIso from "../../../../utilities/time/nowToLocalIso";
import stringIsValidNumber from "../../../../utilities/stringIsValidNumber";
import isoToTimestamp from "../../../../utilities/time/isoToTimestamp";
import useReduxSelector from "../../../../hooks/useReduxSelector";
import apiRequest from "../../../../utilities/api/apiRequest";
import getApiError from "../../../../utilities/api/getApiError";
import smoothTimeDataSeries from "../../../../utilities/smoothTimeDataSeries";
import styles from "./HistoryGraphCardBody.module.scss";

// Specially rendered body for historical graphs.
export default function HistoryGraphCardBody(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [lineSeries, setLineSeries] = useState<TimeDataSeries[] | null>(null);
  const [startDate, setStartDate] = useState<string>(findInitialStartDate(props.numberOfDaysShown));
  const [endDate, setEndDate] = useState<string>(nowToLocalIso(0).split("T")[0]);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const { assetId } = useParams();
  const inAccumulationMode = [
    "HOURLY_ACCUMULATION",
    "DAILY_ACCUMULATION",
    "WEEKLY_ACCUMULATION",
    "MONTHLY_ACCUMULATION",
  ].includes(props.historyGraphType || "");

  // Update the graph by getting API data.
  useEffect(() => {
    async function getGraphData(): Promise<void> {
      // Call the API endpoint once for each attribute that we are interested in.
      const apiRequests: Promise<[Response, HistoryResponseBody]>[] = [];
      props.attributes.forEach((queryAttribute) => {
        const requestBody = {
          startDate: `${startDate}T00:00:00`,
          endDate: isoAddDays(`${endDate}T00:00:00`, 1).split(".")[0],
          companyId: currentUser.companyId,
          attributeCode: queryAttribute.attributeCode,
        };
        setLoading(true);
        const apiRequestPromise = apiRequest(
          `${API}/company/${currentUser.companyId}/asset/${assetId}/history`,
          "POST",
          requestBody
        ) as unknown;
        apiRequests.push(apiRequestPromise as Promise<[Response, HistoryResponseBody]>);
      });

      // Wait for all of the API requests to resolve.
      const resolvedPromises = await Promise.all(apiRequests);

      // Process each API request that have resolved.
      let apiErrorResponse = null;
      const tempSeriesArray: TimeDataSeries[] = [];

      resolvedPromises.forEach(([response, responseBody]) => {
        if (response.ok && responseBody) {
          let unitLongName = "";
          let unitShortName = "";

          const startDateObject = isoToDate(`${startDate}T00:00:00`);
          const endDateObject = isoToDate(isoAddDays(`${endDate}T00:00:00`, 1));

          const historicalAttribute = responseBody;
          if (historicalAttribute !== null && historicalAttribute !== undefined) {
            let name = historicalAttribute.attributeName;
            if (name === null || name === undefined) {
              name = "";
            }

            unitLongName = "";
            unitShortName = "";
            if (unitLongName !== null && unitLongName !== undefined) {
              unitLongName = historicalAttribute.unitLongName;
            }
            if (unitShortName !== null || unitShortName !== undefined) {
              unitShortName = historicalAttribute.unitShortName;
            }

            // Convert incoming data into a series of tuples that supports graphing.
            const series: TimeDataSeries = {
              name: name,
              unitName: unitLongName,
              unitShortName: unitShortName,
              data: [],
            };

            historicalAttribute.dataPoints.forEach((dataPoint: HistoricalDataPoint) => {
              const rowDateObject = isoToLocalDate(dataPoint.dateTimeUtc);

              // If the data point is a boolean, return it as a string representation of a number.
              if (dataPoint.value.toLowerCase() === "true") {
                dataPoint.value = "1";
              } else if (dataPoint.value.toLowerCase() === "false") {
                dataPoint.value = "0";
              }

              if (
                rowDateObject >= startDateObject &&
                rowDateObject < endDateObject &&
                stringIsValidNumber(dataPoint.value)
              ) {
                series.data.push([isoToTimestamp(dataPoint.dateTimeUtc), parseFloat(dataPoint.value)]);
              }
            });

            tempSeriesArray.push(series);
          }
        } else {
          apiErrorResponse = response;
        }
      });

      // Check if there was an error while processing API responses.
      if (apiErrorResponse !== null) {
        setErrorMessage(await getApiError(apiErrorResponse, "Unable to get graph data."));
      } else {
        setErrorMessage("");
      }

      // Check if any attributes are returning no records and warn the user.
      const emptyAttributes: string[] = [];
      tempSeriesArray.forEach((timeSeries) => {
        if (timeSeries.data.length === 0) {
          emptyAttributes.push(timeSeries.name);
        }
      });

      if (emptyAttributes.length === 1) {
        setWarningMessage(`No data could be found for '${emptyAttributes[0]}' in the date range.`);
      } else if (emptyAttributes.length > 1) {
        setWarningMessage(
          `No data could be found for any of the following attributes in the date range: ${emptyAttributes.join(", ")}.`
        );
      } else {
        setWarningMessage("");
      }

      // If a display mode was set, apply it now.
      if (props.historyGraphType === "MOVING_LINE_CHART") {
        const smoothTimeSeriesArray: TimeDataSeries[] = [];
        tempSeriesArray.forEach((timeSeries) => smoothTimeSeriesArray.push(smoothTimeDataSeries(timeSeries)));
        setLineSeries(smoothTimeSeriesArray);
      } else {
        setLineSeries(tempSeriesArray);
      }
      setLoading(false);
    }

    // Make the actual call to get the graph data.
    void getGraphData();
  }, [props.attributes.length, startDate, endDate, currentUser, assetId, props.historyGraphType]);

  // Given the current graphs numberOfDaysShown value and the current date, find the start date.
  function findInitialStartDate(selectedNumberOfDaysShown: number | null): string {
    let numberOfDaysToShow = 1;
    if (selectedNumberOfDaysShown !== null) {
      numberOfDaysToShow = selectedNumberOfDaysShown;
    }
    return nowToLocalIso(1 - numberOfDaysToShow).split("T")[0];
  }

  return (
    <div className={styles.body}>
      <div className={`${styles.body} row mx-1`}>
        <div className="col-6">
          <label>Start Date</label>
          <input
            className="form-control"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="col-6">
          <label>End Date</label>
          <input className="form-control" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="my-2">
          <Spinner loading={loading} inline={true} />
        </div>
      ) : (
        <div className="mx-2">
          {lineSeries !== null && !inAccumulationMode && (
            <LineChart
              series={lineSeries}
              axisMode={props.historyGraphAxisType || "Y_AXIS_UOM"}
              height={GRAPH_HEIGHT_FOR_CARD}
            />
          )}

          {lineSeries !== null && inAccumulationMode && (
            <AccumulationChart
              series={lineSeries}
              accumulationType={(props.historyGraphType || "HOURLY_ACCUMULATION") as AccumulationGraphingMode}
              axisMode={props.historyGraphAxisType || "Y_AXIS_UOM"}
              startDate={startDate}
              endDate={endDate}
              height={GRAPH_HEIGHT_FOR_CARD}
            />
          )}

          <div className="mx-3">
            <Warning message={warningMessage} />
            <Error message={errorMessage} />
          </div>
        </div>
      )}
    </div>
  );
}

interface Props {
  attributes: Attribute[];
  historyGraphAxisType: AxisMode | null;
  historyGraphType: DisplayMode | null;
  numberOfDaysShown: number | null;
  isExtraWide: boolean;
}

interface Attribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  connectedRegAttributeId: number | null;
  connectedAttribute?: Attribute | null;
  unitShortName: string | null;
  unitLongName: string | null;
  icon: string;
  currentValue: string | null;
  currentValueUtc: string | null;
  isHistorical: boolean;
  isGraphable: boolean;
  isStale: boolean;
}

interface HistoryResponseBody {
  attributeName: string;
  unitLongName: string;
  unitShortName: string;
  dataPoints: HistoricalDataPoint[];
}

interface HistoricalDataPoint {
  dateTimeUtc: string;
  value: string;
  isCachedUpload?: boolean;
  uploadDateUtc?: string | null;
}

interface TimeDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: [number, number][];
}

type AccumulationGraphingMode =
  | "HOURLY_ACCUMULATION"
  | "DAILY_ACCUMULATION"
  | "WEEKLY_ACCUMULATION"
  | "MONTHLY_ACCUMULATION";

type DisplayMode =
  | "STANDARD_LINE_CHART"
  | "MOVING_LINE_CHART"
  | "HOURLY_ACCUMULATION"
  | "DAILY_ACCUMULATION"
  | "WEEKLY_ACCUMULATION"
  | "MONTHLY_ACCUMULATION"
  | "CHANGE_LOGS"
  | "ALL_LOGS";

type AxisMode = "Y_AXIS_ATTRIBUTE" | "Y_AXIS_UOM";
